import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { dateKey, monthGridDays, monthKey, parseMonthKey } from "./calendarDates";

export interface ShippingCalendarParamsState {
  /** 表示している月（`2026-10`）。 */
  month: string;
  clientId: number | null;
  /** true で未出荷だけに絞る。 */
  unshippedOnly: boolean;
}

/** 既定の月＝今月。URL に month が無いときはここに落ちる。 */
export function currentMonthKey(): string {
  return monthKey(new Date());
}

/**
 * 出荷カレンダーの表示条件を URL のクエリに持つ。
 *
 * 一覧（[[useProjectListParams]]）と同じ方針。既定値はクエリに載せない（URL を
 * 短く保ち、「条件なし」の状態を共有しやすくする）。
 */
export function useShippingCalendarParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo<ShippingCalendarParamsState>(() => {
    const month = searchParams.get("month");
    const clientId = Number(searchParams.get("client_id"));
    return {
      // 壊れた月（手で URL を直した場合）は今月に落とす。
      month: month && parseMonthKey(month) ? month : currentMonthKey(),
      clientId: Number.isInteger(clientId) && clientId > 0 ? clientId : null,
      unshippedOnly: searchParams.get("unshipped") === "true",
    };
  }, [searchParams]);

  const update = useCallback(
    (patch: Partial<ShippingCalendarParamsState>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const merged = { ...params, ...patch };

          if (merged.month === currentMonthKey()) next.delete("month");
          else next.set("month", merged.month);

          if (merged.clientId == null) next.delete("client_id");
          else next.set("client_id", String(merged.clientId));

          if (merged.unshippedOnly) next.set("unshipped", "true");
          else next.delete("unshipped");

          return next;
        },
        { replace: true },
      );
    },
    [params, setSearchParams],
  );

  /** グリッドに並べる日。API に渡す期間もここから取る。 */
  const days = useMemo(() => monthGridDays(params.month), [params.month]);

  return {
    params,
    update,
    days,
    /** API に渡す期間（グリッドの両端。月の初日・末日ではない）。 */
    dateFrom: dateKey(days[0]),
    dateTo: dateKey(days[days.length - 1]),
    /** API に渡す shipped。絞らないときは未指定。 */
    shippedParam: params.unshippedOnly ? false : undefined,
  };
}
